import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Radio, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import NexusLogo from "@/components/NexusLogo";

const mockChat = [
  { u: "Kaelzin", c: "hsl(217,96%,60%)", m: "boraaa começar a live!" },
  { u: "Mia",     c: "hsl(300,92%,62%)", m: "cheguei 🔥" },
  { u: "Ryan",    c: "hsl(190,92%,55%)", m: "mapa novo tá insano" },
  { u: "Bia",     c: "hsl(40,96%,56%)",  m: "quem topa duo?" },
  { u: "Yuna",    c: "hsl(145,62%,55%)", m: "tô entrando na sala" },
];

const mockChannels = [
  { name: "Cyber Arena", cat: "FPS · 12.4K", live: true },
  { name: "Estúdio Aurora", cat: "Música · 3.1K", live: true },
  { name: "Devs em Ação", cat: "Tech · 1.8K", live: true },
];

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [otp, setOtp] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, otp);
      if (result?.mfa_required) {
        setMfaRequired(true);
        toast.info("Digite o código do aplicativo autenticador");
        return;
      }
      toast.success("Bem-vindo de volta!");
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao entrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,480px)_1fr] bg-background text-foreground">
      {/* LEFT — Form */}
      <div className="flex flex-col justify-between p-8 sm:p-12 border-r border-border relative">
        <NexusLogo />

        <div className="max-w-sm w-full mx-auto lg:mx-0 py-10">
          <div className="mb-8">
            <div className="nx-chip nx-chip-blue mb-4">LIVE · COMMUNITY · CONNECT</div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
              Bem-vindo de <span className="nx-text-gradient">volta!</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Não tem conta?{" "}
              <Link to="/register" className="text-[hsl(var(--nx-cyan))] hover:underline" data-testid="login-goto-register">
                Criar agora
              </Link>
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input" autoComplete="email"
                  placeholder="voce@nexus.live"
                  className="pl-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]"
                />
              </div>
            </div>

            {mfaRequired && (
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Código de autenticação
                </Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  minLength={6}
                  maxLength={6}
                  placeholder="000000"
                  className="h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))] tracking-[0.4em] text-center"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password" type={showPwd ? "text" : "password"} required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input" autoComplete="current-password"
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]"
                />
                <button
                  type="button" onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="login-toggle-password"
                  aria-label="Mostrar senha"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full h-11 rounded-xl nx-btn-blue border-0 font-semibold text-base"
              data-testid="login-submit-btn"
            >
              {loading ? "entrando…" : (
                <>Entrar <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>

            <div className="flex items-center gap-3 py-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">ou</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="flex items-center justify-between text-sm">
              <Link to="/register" className="text-foreground hover:text-[hsl(var(--nx-cyan))]" data-testid="login-create-account-link">
                Criar conta
              </Link>
              <Link to="/login" className="text-muted-foreground hover:text-foreground" data-testid="login-forgot-link">
                Esqueci minha senha
              </Link>
            </div>
          </form>
        </div>

        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
          © {new Date().getFullYear()} Nexus Live · v1.0
        </p>
      </div>

      {/* RIGHT — Live preview */}
      <div className="hidden lg:block relative overflow-hidden bg-[hsl(var(--nx-panel))]">
        <div className="absolute inset-0 nx-grid-bg opacity-40" />
        <div className="absolute -top-40 -right-24 w-[480px] h-[480px] rounded-full bg-[hsl(var(--nx-blue)/0.22)] blur-[120px]" />
        <div className="absolute bottom-0 -left-24 w-[400px] h-[400px] rounded-full bg-[hsl(var(--nx-cyan)/0.16)] blur-[100px]" />

        <div className="relative z-10 h-full flex flex-col p-10 xl:p-14">
          <div className="max-w-xl">
            <h2 className="font-display font-bold text-3xl xl:text-[38px] leading-[1.05]">
              Transmissões ao vivo,<br />
              comunidade e <span className="nx-text-gradient">conexões reais.</span>
            </h2>
            <p className="mt-3 text-muted-foreground text-sm max-w-lg">
              Servidores, canais em tempo real e lives descobertas numa única identidade. Sem clonagem, sem ruído — só pessoas conversando e criando.
            </p>
          </div>

          {/* Live card mock */}
          <div className="mt-10 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
            <div className="nx-glass rounded-2xl overflow-hidden relative">
              <div className="aspect-[16/10] bg-gradient-to-br from-[hsl(217,60%,20%)] via-[hsl(224,40%,14%)] to-[hsl(300,50%,18%)] relative overflow-hidden">
                <div className="absolute inset-0 nx-scanline" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                    <Radio className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[hsl(var(--nx-red))] text-white text-[10px] font-bold uppercase tracking-widest">
                    <span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO
                  </span>
                  <span className="px-2 py-1 rounded-md bg-black/50 text-white text-[10px] font-semibold tracking-wide">
                    12.4K
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-white font-display font-semibold text-lg leading-tight">Cyber Arena · Grand Final</div>
                  <div className="text-white/70 text-xs">by @kaelzin · FPS competitivo</div>
                </div>
              </div>

              {/* stream chat mock */}
              <div className="p-4 border-t border-[hsl(var(--nx-border))]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">Chat da transmissão</div>
                <ul className="space-y-1.5 max-h-[140px] overflow-hidden">
                  {mockChat.map((m, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-semibold" style={{color: m.c}}>{m.u}</span>
                      <span className="text-muted-foreground"> · </span>
                      <span>{m.m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* active channels */}
            <div className="nx-glass rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 font-semibold">Canais ativos</div>
              <ul className="space-y-2">
                {mockChannels.map((ch, i) => (
                  <li key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] grid place-items-center text-white font-bold text-sm">
                      {ch.name[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{ch.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{ch.cat}</div>
                    </div>
                    {ch.live && <span className="nx-chip nx-chip-red !py-0.5 !px-2 !text-[9px]">LIVE</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-auto pt-8 grid grid-cols-3 gap-4">
            {[
              { icon: Users, t: "Comunidades ativas", s: "servidores organizados por interesse" },
              { icon: Radio, t: "Conversas em tempo real", s: "chat, voz, presença ao vivo" },
              { icon: Sparkles, t: "Lives de qualidade", s: "descoberta feita para criadores" },
            ].map((f) => (
              <div key={f.t} className="nx-panel-2 p-4">
                <f.icon className="w-5 h-5 text-[hsl(var(--nx-cyan))]" />
                <div className="mt-2 text-sm font-semibold">{f.t}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{f.s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
